import React, { useState, useEffect } from 'react';
import Calling from '../../api/Calling';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { TextField, Skeleton, CircularProgress } from '@mui/material';
import UploadButton from '../../components/ul/UploadButton';
import ResetButton from '../../components/ul/ResetButton';
import JsonDropzone from '../../components/ul/JsonDropzone';
import NotFound from '../../components/ul/NotFound';
import LottieLoad from '../../components/LotteiLoad';

// Icons (lucide-react)
import {
    Pencil,
    CheckCircle2,
    XCircle,
    Info,
    RefreshCw,
    Clock,
    UploadCloud,
    FileText,
    FileJson,
} from 'lucide-react';

// Human-readable file size
const formatFileSize = (bytes) => {
    if (bytes === undefined || bytes === null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// Shared MUI light-field styling (rounded, green focus)
const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-main-blue, #34c759)',
        },
    },
};

const JsonManager = () => {
    const [loader, setLoader] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileSelectedAt, setFileSelectedAt] = useState(null);
    const [replacementFile, setReplacementFile] = useState({});
    const [error, setError] = useState(null);
    const [jsonFiles, setJsonFiles] = useState([]);
    const [editMode, setEditMode] = useState({});
    const [newFileName, setNewFileName] = useState('');
    const navigate = useNavigate();

    // New states for editing
    const [editingName, setEditingName] = useState({});
    // eslint-disable-next-line no-unused-vars
    const [currentTime, setCurrentTime] = useState(new Date().toLocaleString());
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const allowedFileTypes = ['application/json'];
    const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

    useEffect(() => {
        fetchJsonFiles();

        // Update current time every minute
        const timeInterval = setInterval(() => {
            setCurrentTime(new Date().toLocaleString());
        }, 60000);

        return () => clearInterval(timeInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchJsonFiles = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getJsonManage();
            if (resp?.status) {
                setJsonFiles(resp?.data);
                setLastUpdated(new Date());
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Fetch error:", error.response?.data || error.message);
            navigate('/Error');
        } finally {
            setLoader(false);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const resp = await Calling.getJsonManage();
            if (resp?.status) {
                setJsonFiles(resp?.data);
                setLastUpdated(new Date());
                // toast.success("Data refreshed successfully");
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Refresh error:", error.response?.data || error.message);
            toast.error("Failed to refresh data");
        } finally {
            setIsRefreshing(false);
        }
    };

    const validateJsonFile = (file) => {
        if (!file) {
            return { isValid: false, error: 'No file selected.' };
        }

        if (!allowedFileTypes.includes(file.type)) {
            return { isValid: false, error: 'Invalid file type. Only JSON files are allowed.' };
        }

        if (file.size > MAX_FILE_SIZE) {
            return { isValid: false, error: 'File size exceeds the 1 MB limit.' };
        }

        return { isValid: true, error: null };
    };

    // Accepts a File directly (from JsonDropzone: drag / click / paste). null clears the selection.
    const processNewJsonFile = (file) => {
        setError(null);
        setSelectedFile(null);
        setFileSelectedAt(null);

        if (!file) return; // cleared via the dropzone remove (x)

        const validation = validateJsonFile(file);
        if (!validation.isValid) {
            setError(validation.error);
            toast.error(validation.error);
            return;
        }

        // Validate JSON structure
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                JSON.parse(e.target.result);
                setSelectedFile(file);
                setFileSelectedAt(new Date());
            } catch (error) {
                const errorMsg = 'Invalid JSON structure. Please check your file.';
                setError(errorMsg);
                toast.error(errorMsg);
            }
        };
        reader.readAsText(file);
    };

    const processReplacementJsonFile = (file, fileId) => {
        // Remove any previous selection/error for this file
        setReplacementFile(prev => ({
            ...prev,
            [fileId]: { file: null, error: null }
        }));

        if (!file) return; // cleared via the dropzone remove (x)

        const validation = validateJsonFile(file);
        if (!validation.isValid) {
            setReplacementFile(prev => ({
                ...prev,
                [fileId]: { file: null, error: validation.error }
            }));
            toast.error(validation.error);
            return;
        }

        // Validate JSON structure
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                JSON.parse(e.target.result);
                setReplacementFile(prev => ({
                    ...prev,
                    [fileId]: { file, error: null }
                }));
            } catch (error) {
                const errorMsg = 'Invalid JSON structure. Please check your file.';
                setReplacementFile(prev => ({
                    ...prev,
                    [fileId]: { file: null, error: errorMsg }
                }));
                toast.error(errorMsg);
            }
        };
        reader.readAsText(file);
    };

    const resetForm = () => {
        setSelectedFile(null);
        setFileSelectedAt(null);
        setError(null);
        setNewFileName('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedFile) {
            setError('Please select a valid JSON file before submitting.');
            return;
        }

        // Check if name is provided
        const fileName = newFileName.trim() || selectedFile.name || 'Unnamed JSON';

        setLoader(true);
        try {
            const formData = new FormData();
            formData.append('image', selectedFile);
            formData.append('name', fileName);

            const resp = await Calling.addJsonManager(formData);

            if (resp?.status) {
                toast.success(resp?.message);
                resetForm();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Submission error:", error.response?.data || error.message);
            toast.error("Something went wrong.");
        } finally {
            setLoader(false);
            await fetchJsonFiles();
        }
    };

    const handleEditToggle = (fileId) => {
        setEditMode({
            ...editMode,
            [fileId]: !editMode[fileId]
        });

        // Initialize editing name with current name
        if (!editMode[fileId]) {
            const file = jsonFiles.find(f => f._id === fileId);
            setEditingName({
                ...editingName,
                [fileId]: file?.j_name || ''
            });

            // Reset any replacement file
            setReplacementFile(prev => ({
                ...prev,
                [fileId]: { file: null, error: null }
            }));
        }
    };

    const handleNameChange = (fileId, value) => {
        setEditingName({
            ...editingName,
            [fileId]: value
        });
    };

    const handleUpdate = async (fileId) => {
        setLoader(true);
        try {
            const formData = new FormData();

            // Add name if it was edited
            if (editingName[fileId]) {
                formData.append('name', editingName[fileId]);
            }

            // Add replacement JSON file if provided
            if (replacementFile[fileId]?.file) {
                formData.append('image', replacementFile[fileId].file);
            }

            // Call the update API
            const resp = await Calling.updateJsonmanager(fileId, formData);

            if (resp?.status) {
                toast.success("JSON file updated successfully");
                setEditMode({
                    ...editMode,
                    [fileId]: false
                });
                // Reset replacement file after successful update
                setReplacementFile(prev => ({
                    ...prev,
                    [fileId]: { file: null, error: null }
                }));
                await fetchJsonFiles();
            } else {
                toast.error(resp?.message || "Update failed");
            }
        } catch (error) {
            console.error("Update error:", error);
            toast.error("Something went wrong during update.");
        } finally {
            setLoader(false);
        }
    };

    // Full-page skeleton ONLY on the initial load (no data yet). For subsequent
    // operations (upload/update refresh) we keep the real tree mounted — swapping the
    // whole tree while AnimatePresence blocks are mid-exit is what caused the
    // "Failed to execute 'removeChild'" crash.
    if (loader && jsonFiles.length === 0) {
        return (
            <div className='w-full text-main-font'>
                <div className='bg-main-white rounded-xl pb-2 shadow-lg'>
                    <div className='p-3 sm:p-6'>
                        {/* Header skeleton */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 gap-3 mb-5">
                            <div className="flex items-center gap-3">
                                <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '12px' }} />
                                <Skeleton variant="text" width={180} height={32} />
                            </div>
                            <div className="flex items-center gap-3">
                                <Skeleton variant="rounded" width={180} height={40} sx={{ borderRadius: '8px' }} />
                                <Skeleton variant="rounded" width={48} height={40} sx={{ borderRadius: '8px' }} />
                            </div>
                        </div>
                        <hr className='border-main-border' />

                        {/* Upload-form card skeleton */}
                        <div className="mt-6 bg-main-th p-5 rounded-xl border border-main-border">
                            <Skeleton variant="text" width={220} height={28} sx={{ mb: 2 }} />
                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                <Skeleton variant="rounded" height={80} sx={{ borderRadius: '10px' }} />
                                <Skeleton variant="rounded" height={80} sx={{ borderRadius: '10px' }} />
                            </div>
                            <div className='flex gap-4 mt-4'>
                                <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: '8px' }} />
                                <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: '8px' }} />
                            </div>
                        </div>

                        {/* Grid card skeletons */}
                        <div className='mt-8'>
                            <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
                            <div className='bg-main-bg p-5 rounded-xl border border-main-border'>
                                <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5'>
                                    {[...Array(4)].map((_, idx) => (
                                        <div key={idx} className='bg-main-white border border-main-border rounded-xl p-4'>
                                            <Skeleton variant="rounded" height={240} sx={{ borderRadius: '8px', mb: 2 }} />
                                            <Skeleton variant="text" width="70%" height={24} />
                                            <Skeleton variant="rounded" height={60} sx={{ borderRadius: '8px', mt: 1 }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className='w-full text-main-font'
        >
            <div className='bg-main-white rounded-xl pb-2 shadow-lg'>
                <div className='p-3 sm:p-6'>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 gap-3 mb-5">
                        {/* Title with Icon and Badge */}
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold flex items-center gap-3 text-main-font">
                                <div className="relative p-2.5 bg-main-blue rounded-xl shadow-lg">
                                    <FileJson className="text-white" size={20} />
                                    {jsonFiles.length > 0 && (
                                        <span className="absolute -top-2 -right-2 min-w-[20px] h-5 bg-main-exBlue text-white text-[10px] font-semibold rounded-full flex items-center justify-center shadow-md">
                                            {jsonFiles.length > 99 ? '99+' : jsonFiles.length}
                                        </span>
                                    )}
                                </div>
                                <span>JSON Manager</span>
                            </h1>
                        </div>

                        {/* Actions - Last Updated & Refresh */}
                        <div className="flex items-center gap-3">
                            <div className="bg-main-white px-4 py-2 rounded-lg shadow-sm border border-main-border text-sm text-main-subfont flex items-center gap-2">
                                <Clock className="text-main-subfont" size={16} />
                                <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                            </div>
                            <button
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                                className={`bg-main-blue hover:bg-main-blue/80 text-white px-4 py-2 rounded-lg shadow-sm border border-main-blue text-sm font-medium flex items-center gap-2 transition-all duration-200 ${isRefreshing ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-md'}`}
                                title="Refresh Data"
                            >
                                {isRefreshing ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <RefreshCw size={16} />
                                )}
                            </button>
                        </div>
                    </div>
                    <hr className='border-main-border' />

                    {/* Upload Section - Always visible */}
                    <div className="mt-6 bg-main-th p-5 rounded-xl shadow-sm border border-main-border">
                        <h2 className="text-lg font-semibold text-main-font mb-4 flex items-center gap-2">
                            <UploadCloud className="text-main-blue" size={20} />
                            Upload New JSON File
                        </h2>
                        <form onSubmit={handleSubmit} className='space-y-4'>
                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                <div>
                                    <label className="block font-medium text-main-font mb-2">
                                        File Name
                                    </label>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={newFileName}
                                        onChange={(e) => setNewFileName(e.target.value)}
                                        placeholder={selectedFile ? selectedFile.name : "Enter custom name for the JSON file"}
                                        required={true}
                                        sx={fieldSx}
                                    />
                                    <p className="text-[12px] mt-1 text-main-subfont">
                                        If left empty, the original file name will be used.
                                    </p>
                                </div>

                                <div>
                                    <label className="font-medium text-main-font mb-2 flex items-center">
                                        JSON File
                                        <span className='ml-2 text-sm text-main-blue bg-main-thHover px-2 py-0.5 rounded-full'>
                                            Max size: 1 MB
                                        </span>
                                    </label>
                                    <JsonDropzone file={selectedFile} onFile={processNewJsonFile} />
                                    <p className={`text-[12px] mt-2 ${error ? 'text-main-red' : 'text-main-subfont'}`}>
                                        {error || 'Drag, click, or paste — only valid JSON files (max 1 MB) are allowed.'}
                                    </p>
                                </div>
                            </div>

                            <div className='flex space-x-4 py-2'>
                                <UploadButton />
                                <ResetButton onClick={resetForm} />
                            </div>

                            <AnimatePresence>
                                {selectedFile && (
                                    <motion.div
                                        key="ready-to-upload"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        transition={{ duration: 0.25 }}
                                        className="bg-main-thHover border border-main-blue rounded-lg p-3 flex items-center gap-3 overflow-hidden"
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-main-blue/10 text-main-blue flex items-center justify-center flex-shrink-0">
                                            <FileJson size={20} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-main-font">Ready to upload</p>
                                            <p className="text-sm text-main-subfont truncate">{selectedFile.name}</p>
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-main-subfont">
                                                <span className="inline-flex items-center gap-1">
                                                    <FileText size={12} className="text-main-blue" />
                                                    {formatFileSize(selectedFile.size)}
                                                </span>
                                                {fileSelectedAt && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <Clock size={12} className="text-main-blue" />
                                                        Selected at {fileSelectedAt.toLocaleTimeString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </form>
                    </div>

                    {/* Manage Files Section - Always visible */}
                    <div className='mt-8'>
                        <h2 className="text-lg font-semibold text-main-font mb-4 flex items-center gap-2">
                            <FileText className="text-main-blue" size={20} />
                            Manage JSON Files
                        </h2>
                        <div className='bg-main-bg p-5 rounded-xl shadow-md border border-main-border'>
                            {jsonFiles.length > 0 ? (
                                <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5'>
                                    {jsonFiles.map((file, index) => (
                                        <motion.div
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.4) }}
                                            whileHover={{ y: -4 }}
                                            className={`${editMode[file._id]
                                                ? 'bg-main-th border-main-blue ring-2 ring-main-thHover'
                                                : 'bg-main-white hover:bg-main-bg border-main-border'
                                                } border rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300`}
                                            key={file._id}
                                        >
                                            {/* Enhanced Lottie animation display */}
                                            <div className="flex justify-center mb-4">
                                                <div className="w-full h-60 bg-main-bg rounded-lg flex items-center justify-center overflow-hidden shadow-inner p-2 border border-main-border">
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <LottieLoad url={file?.j_json} />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* File info and controls */}
                                            <div className='flex justify-between items-center'>
                                                {/* Name field */}
                                                {editMode[file._id] ? (
                                                    <div className="mb-3 w-full">
                                                        <label className="block text-main-font text-sm font-medium mb-1">
                                                            File Name:
                                                        </label>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            value={editingName[file._id] || ''}
                                                            onChange={(e) => handleNameChange(file._id, e.target.value)}
                                                            placeholder="Enter file name"
                                                            required={true}
                                                            sx={fieldSx}
                                                        />
                                                    </div>
                                                ) : (
                                                    <h3 className="text-lg font-semibold text-main-font truncate">{file?.j_name}</h3>
                                                )}

                                                <div className="flex items-center space-x-2 ml-2">
                                                    {editMode[file._id] ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleUpdate(file._id)}
                                                                className="p-2 bg-main-thHover text-main-blue rounded-full hover:bg-main-th shadow-sm transition-colors"
                                                                title="Save changes"
                                                            >
                                                                <CheckCircle2 size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEditToggle(file._id)}
                                                                className="p-2 bg-red-50 text-main-red rounded-full hover:bg-red-100 shadow-sm transition-colors"
                                                                title="Cancel"
                                                            >
                                                                <XCircle size={18} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleEditToggle(file._id)}
                                                            className="p-2 bg-main-th text-main-blue rounded-full hover:bg-main-thHover shadow-sm transition-colors"
                                                            title="Edit file"
                                                        >
                                                            <Pencil size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Replace JSON file option (only in edit mode) */}
                                            <AnimatePresence>
                                                {editMode[file._id] && (
                                                    <motion.div
                                                        key={`replace-${file._id}`}
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        transition={{ duration: 0.25 }}
                                                        className="mb-3 mt-3 p-3 bg-main-white rounded-lg border border-main-border overflow-hidden"
                                                    >
                                                        <label className="block text-main-font text-sm font-medium mb-2">
                                                            Replace JSON:
                                                        </label>
                                                        <JsonDropzone
                                                            file={replacementFile[file._id]?.file || null}
                                                            onFile={(f) => processReplacementJsonFile(f, file._id)}
                                                        />
                                                        {replacementFile[file._id]?.error && (
                                                            <p className="text-xs text-main-red mt-2 bg-red-50 p-2 rounded-lg">
                                                                {replacementFile[file._id].error}
                                                            </p>
                                                        )}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>

                                            {/* Timestamps */}
                                            <div className={`p-3 rounded-lg ${editMode[file._id] ? 'bg-main-white border border-main-border' : 'bg-main-bg'} mt-2`}>
                                                <div className="flex items-center text-main-subfont text-sm gap-2">
                                                    <Clock size={14} />
                                                    <span className="font-medium text-main-font">Created:</span>
                                                    <span>{new Date(file.createdAt).toLocaleString()}</span>
                                                </div>
                                                {file.updatedAt && file.updatedAt !== file.createdAt && (
                                                    <div className="flex items-center text-main-subfont text-sm mt-1 gap-2">
                                                        <Clock size={14} />
                                                        <span className="font-medium text-main-font">Updated:</span>
                                                        <span>{new Date(file.updatedAt).toLocaleString()}</span>
                                                    </div>
                                                )}

                                                {/* Update will add current time */}
                                                <AnimatePresence>
                                                    {editMode[file._id] && (
                                                        <motion.div
                                                            key={`saving-${file._id}`}
                                                            initial={{ opacity: 0, height: 0 }}
                                                            animate={{ opacity: 1, height: 'auto' }}
                                                            transition={{ duration: 0.25 }}
                                                            className="flex items-center text-main-blue text-sm mt-2 gap-2 overflow-hidden"
                                                        >
                                                            <Info size={14} />
                                                            <p className="italic">
                                                                Saving will update to: {new Date().toLocaleString()}
                                                            </p>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            ) : (
                                <div className="bg-main-white p-6 rounded-xl shadow-sm border border-main-border">
                                    <div className="text-center py-8">
                                        <NotFound />
                                        <p className="text-main-subfont mt-4">No JSON files uploaded yet. Use the upload form above to add your first file.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default JsonManager;
